// M01/M03/M11/M12 — market intelligence calculators & planners.
import { useMemo, useState } from "react";
import { tamSamSom, journeyFriction, trendReadiness, weightedMatrix, inr, pct } from "./engines";
import { Field, SliderField, ResultRow, ToolSection, Note, useNumbers, Radar } from "./ui";

/* ---------- M01 Honest TAM–SAM–SOM ---------- */
export function ToolM01() {
  const [v, set] = useNumbers({ accounts: 4000, frequencyPerYear: 6, avgValue: 18000, geographyPct: 65, segmentFitPct: 45, capabilityPct: 70 });
  const r = useMemo(() => tamSamSom({ ...v, shareScenariosPct: [1, 3, 8] }), [v]);
  return (
    <div data-testid="tool-M01" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="text-xs font-medium uppercase tracking-widest text-zinc-500">Bottom-up build</div>
        <Field label="Reachable buyer accounts / households" hint="From a named list, census or category panel — not a top-down %." value={v.accounts} onChange={set("accounts")} testid="m01-accounts" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Purchases per year" value={v.frequencyPerYear} onChange={set("frequencyPerYear")} testid="m01-freq" />
          <Field label="Average value per purchase (₹)" value={v.avgValue} onChange={set("avgValue")} testid="m01-value" />
        </div>
        <div className="border-t border-black/10 pt-5 space-y-5">
          <div className="text-xs font-medium uppercase tracking-widest text-zinc-500">SAM filters — be honest</div>
          <SliderField label="Geographies you will actually serve" value={v.geographyPct} onChange={set("geographyPct")} testid="m01-geo" />
          <SliderField label="Segments your offer genuinely fits" value={v.segmentFitPct} onChange={set("segmentFitPct")} testid="m01-seg" />
          <SliderField label="Share you can reach with current capability" hint="Channels, certifications, service coverage." value={v.capabilityPct} onChange={set("capabilityPct")} testid="m01-cap" />
        </div>
      </div>
      <div>
        <div data-testid="m01-result">
          <ResultRow label="TAM — total theoretical demand" value={inr(r.tam)} />
          <ResultRow label={`SAM — serviceable (${r.samPctOfTam}% of TAM)`} value={inr(r.sam)} strong />
          {r.som.map((s) => (
            <ResultRow key={s.sharePct} label={`SOM at ${s.sharePct}% share`} value={inr(s.value)} tone={s.sharePct >= 8 ? "bad" : undefined} />
          ))}
        </div>
        {r.warnings.length > 0 && (
          <div className="mt-5 space-y-2" data-testid="m01-warnings">
            {r.warnings.map((w, i) => <Note key={i} tone="warn">{w}</Note>)}
          </div>
        )}
        <ToolSection title="Where double-counting hides">
          <ul className="list-disc pl-5 space-y-1.5 text-sm text-zinc-600">
            <li>Channel sales counted at both distributor and retail value.</li>
            <li>The same buyer appearing in two segment definitions.</li>
            <li>Category reports that already include your substitute category.</li>
            <li>Multiplying penetration % by spend % taken from different studies.</li>
          </ul>
        </ToolSection>
      </div>
    </div>
  );
}

/* ---------- M03 Segment Attractiveness Matrix ---------- */
const M03_CRITERIA = [
  { key: "size", label: "Segment size & spend" },
  { key: "growth", label: "Growth rate" },
  { key: "margin", label: "Margin potential" },
  { key: "access", label: "Our access (channel/brand)" },
  { key: "fit", label: "Capability fit" },
  { key: "rivalry", label: "Weak competition" },
];
const M03_DEFAULT = [
  { name: "Urban premium families", scores: { size: 4, growth: 4, margin: 5, access: 3, fit: 4, rivalry: 2 } },
  { name: "Tier-2 value seekers", scores: { size: 5, growth: 3, margin: 2, access: 4, fit: 3, rivalry: 2 } },
  { name: "Institutional buyers", scores: { size: 3, growth: 2, margin: 3, access: 2, fit: 3, rivalry: 4 } },
];
export function ToolM03() {
  const [segments, setSegments] = useState(M03_DEFAULT);
  const [weights, setWeights] = useState({ size: 3, growth: 3, margin: 2, access: 2, fit: 2, rivalry: 1 });
  const ranked = useMemo(() => weightedMatrix({ items: segments, criteria: M03_CRITERIA, weights }), [segments, weights]);
  const setScore = (i, k, val) => setSegments((s) => s.map((x, xi) => (xi === i ? { ...x, scores: { ...x.scores, [k]: val } } : x)));
  const setW = (k) => (val) => setWeights((w) => ({ ...w, [k]: val }));
  return (
    <div data-testid="tool-M03">
      <div className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 space-y-6">
          {segments.map((s, i) => (
            <div key={i} className="border border-zinc-200 p-5" data-testid={`m03-segment-${i}`}>
              <input
                value={s.name}
                onChange={(e) => setSegments((arr) => arr.map((x, xi) => (xi === i ? { ...x, name: e.target.value } : x)))}
                className="w-full font-medium text-sm outline-none border-b border-transparent focus:border-black pb-1"
                data-testid={`m03-name-${i}`}
              />
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                {M03_CRITERIA.map((c) => (
                  <SliderField key={c.key} label={c.label} value={s.scores[c.key]} onChange={(v) => setScore(i, c.key, v)} min={0} max={5} suffix="" testid={`m03-s-${i}-${c.key}`} />
                ))}
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setSegments((s) => [...s, { name: `Segment ${s.length + 1}`, scores: Object.fromEntries(M03_CRITERIA.map((c) => [c.key, 3])) }])}
            className="text-sm font-medium underline underline-offset-4"
            data-testid="m03-add"
          >
            + Add segment
          </button>
        </div>
        <div className="lg:col-span-2">
          <div className="text-xs font-medium uppercase tracking-widest text-zinc-500">Weights — what matters most now</div>
          <div className="mt-3 space-y-4">
            {M03_CRITERIA.map((c) => (
              <SliderField key={c.key} label={c.label} value={weights[c.key]} onChange={setW(c.key)} min={0} max={5} suffix="" testid={`m03-w-${c.key}`} />
            ))}
          </div>
          <div className="mt-8 border-t-2 border-black pt-6" data-testid="m03-result">
            <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Priority ranking</div>
            {ranked.map((r) => (
              <ResultRow key={r.name} label={`#${r.rank} ${r.name}`} value={`${r.score}%`} strong={r.rank === 1} tone={r.rank === 1 ? "good" : undefined} />
            ))}
          </div>
          <Note>Scores are judgments, not measurements. Flag any score you can't defend with evidence — that flag is your research plan.</Note>
        </div>
      </div>
    </div>
  );
}

/* ---------- M11 Journey Friction Mapper ---------- */
const M11_DEFAULT = [
  { stage: "Aware of brand", entrants: 100000, dropPct: 55, avgValue: 900 },
  { stage: "Consider / shortlist", dropPct: 40, avgValue: 900 },
  { stage: "Visit store / PDP", dropPct: 35, avgValue: 900 },
  { stage: "Add to cart / ask price", dropPct: 45, avgValue: 900 },
  { stage: "Purchase", dropPct: 20, avgValue: 900 },
];
export function ToolM11() {
  const [stages, setStages] = useState(M11_DEFAULT);
  const r = useMemo(() => journeyFriction(stages), [stages]);
  const setS = (i, k) => (val) => setStages((s) => s.map((x, xi) => (xi === i ? { ...x, [k]: val } : x)));
  return (
    <div data-testid="tool-M11" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        <Field label="People entering the journey per month" value={stages[0].entrants} onChange={setS(0, "entrants")} testid="m11-entrants" />
        {stages.map((s, i) => (
          <div key={i} className="border border-zinc-200 p-4 grid grid-cols-12 gap-3 items-end" data-testid={`m11-stage-${i}`}>
            <div className="col-span-5">
              <input value={s.stage} onChange={(e) => setS(i, "stage")(e.target.value)} className="w-full text-sm font-medium outline-none border-b border-transparent focus:border-black" data-testid={`m11-name-${i}`} />
            </div>
            <div className="col-span-4">
              <Field label="Drop-off %" value={s.dropPct} onChange={setS(i, "dropPct")} testid={`m11-drop-${i}`} />
            </div>
            <div className="col-span-3">
              <Field label="Avg value ₹" value={s.avgValue} onChange={setS(i, "avgValue")} testid={`m11-val-${i}`} />
            </div>
          </div>
        ))}
        <button type="button" onClick={() => setStages((s) => [...s, { stage: `Stage ${s.length + 1}`, dropPct: 30, avgValue: s[0].avgValue }])} className="text-sm font-medium underline underline-offset-4" data-testid="m11-add">+ Add stage</button>
      </div>
      <div>
        <div data-testid="m11-result">
          <div className="space-y-2">
            {r.rows.map((row, i) => {
              const maxLeak = Math.max(...r.rows.map((x) => x.leakValue), 1);
              return (
                <div key={i} className="grid grid-cols-12 items-center gap-3" data-testid={`m11-row-${i}`}>
                  <span className="col-span-4 text-xs text-zinc-600 truncate">{row.stage}</span>
                  <div className="col-span-5 h-6 bg-zinc-100">
                    <div className={`h-full ${row.stage === r.biggestLeak ? "bg-red-700" : "bg-zinc-500"}`} style={{ width: `${Math.max(2, (row.leakValue / maxLeak) * 100)}%` }} />
                  </div>
                  <span className="col-span-3 text-right text-xs tabular-nums text-zinc-700">{inr(row.leakValue)} /mo</span>
                </div>
              );
            })}
          </div>
          <div className="mt-6">
            <ResultRow label="Reaching the end (per month)" value={r.survivors.toLocaleString("en-IN")} />
            <ResultRow label="Expected value leaking / month" value={inr(r.totalLeak)} strong tone="bad" />
            <ResultRow label="Largest leak (value-weighted)" value={r.biggestLeak || "—"} />
          </div>
        </div>
        <Note tone="warn">Fix the largest leak first. A 5-point improvement at the biggest stage usually beats a 20-point fix at a small one.</Note>
      </div>
    </div>
  );
}

/* ---------- M12 Trend Readiness Radar ---------- */
const M12_DEFAULT = [
  { trend: "Quick-commerce shift", relevance: 5, velocity: 5, readiness: 2 },
  { trend: "Premiumisation of staples", relevance: 4, velocity: 3, readiness: 3 },
  { trend: "Ingredient transparency", relevance: 3, velocity: 3, readiness: 2 },
  { trend: "Regional language commerce", relevance: 4, velocity: 4, readiness: 1 },
  { trend: "Sustainable packaging norms", relevance: 2, velocity: 2, readiness: 2 },
];
export function ToolM12() {
  const [trends, setTrends] = useState(M12_DEFAULT);
  const r = useMemo(() => trendReadiness(trends), [trends]);
  const setT = (i, k) => (val) => setTrends((s) => s.map((x, xi) => (xi === i ? { ...x, [k]: val } : x)));
  const top5 = trends.slice(0, 5);
  return (
    <div data-testid="tool-M12" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        {trends.map((t, i) => (
          <div key={i} className="border border-zinc-200 p-4" data-testid={`m12-trend-${i}`}>
            <input value={t.trend} onChange={(e) => setT(i, "trend")(e.target.value)} className="w-full text-sm font-medium outline-none border-b border-transparent focus:border-black pb-1" data-testid={`m12-name-${i}`} />
            <div className="mt-3 grid grid-cols-3 gap-3">
              <SliderField label="Relevance" value={t.relevance} onChange={setT(i, "relevance")} min={0} max={5} suffix="" testid={`m12-rel-${i}`} />
              <SliderField label="Velocity" value={t.velocity} onChange={setT(i, "velocity")} min={0} max={5} suffix="" testid={`m12-vel-${i}`} />
              <SliderField label="Readiness" value={t.readiness} onChange={setT(i, "readiness")} min={0} max={5} suffix="" testid={`m12-ready-${i}`} />
            </div>
          </div>
        ))}
        <button type="button" onClick={() => setTrends((s) => [...s, { trend: `Trend ${s.length + 1}`, relevance: 3, velocity: 3, readiness: 3 }])} className="text-sm font-medium underline underline-offset-4" data-testid="m12-add">+ Add trend</button>
      </div>
      <div>
        <Radar dims={top5.map((t) => ({ label: t.trend.length > 14 ? t.trend.slice(0, 13) + "…" : t.trend, score: (t.readiness / 5) * 100 }))} />
        <div className="mt-6" data-testid="m12-result">
          <div className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Exposure vs readiness — sorted by gap</div>
          {r.rows.map((row) => (
            <ResultRow key={row.trend} label={row.trend} value={`exposure ${row.exposure}/25 · ready ${row.readinessPct}%`} tone={row.urgent ? "bad" : undefined} />
          ))}
          <ResultRow label="Urgent gaps (≥10)" value={String(r.urgentCount)} strong tone={r.urgentCount ? "bad" : "good"} />
        </div>
        <Note>Exposure = relevance × velocity (how hard this hits you). Readiness = what you could ship in 90 days. Urgent gaps need an owner this quarter, not a watch-list.</Note>
      </div>
    </div>
  );
}
